import 'extensions/ui-components/x-loader';
import {defineCustomElement} from 'extensions/ui-utils';
import {html, LitElement, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import stylesheet from './x-builderset-group.scss?as=stylesheet';

export class BuilderSetGroup extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	// eslint-disable-next-line max-lines-per-function
	static get properties () {
		return {
			baseSubtotal: {
				attribute: 'base-subtotal',
				type: Number
			},
			code: {
				type: String
			},
			descrip: {
				type: String
			},
			formattedBaseSubtotal: {
				attribute: 'formatted-base-subtotal',
				type: String
			},
			formattedPrice: {
				attribute: 'formatted-price',
				type: String
			},
			formattedSubtotal: {
				attribute: 'formatted-subtotal',
				type: String
			},
			hidePricing: {
				attribute: 'hide-pricing',
				type: Boolean
			},
			id: {
				type: String
			},
			image: {
				type: String
			},
			loading: {
				type: Boolean
			},
			name: {
				type: String
			},
			price: {
				type: Number
			},
			subtotal: {
				type: Number
			},
			updateUrl: {
				attribute: 'update-url',
				type: String
			}
		};
	}

	// eslint-disable-next-line max-lines-per-function
	constructor () {
		super();

		/**
		 * @type {number}
		 */
		this.baseSubtotal = 0;

		/**
		 * @type {string}
		 */
		this.code = '';

		/**
		 * @type {string}
		 */
		this.descrip = '';

		/**
		 * @type {string}
		 */
		this.formattedBaseSubtotal = '';

		/**
		 * @type {string}
		 */
		this.formattedPrice = '';

		/**
		 * @type {string}
		 */
		this.formattedSubtotal = '';

		/**
		 * @type {boolean}
		 */
		this.hidePricing = false;

		/**
		 * @type {string}
		 */
		this.id = '';

		/**
		 * @type {string}
		 */
		this.image = '';

		/**
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * @type {string}
		 */
		this.name = '';

		/**
		 * @type {number}
		 */
		this.price = 0;

		/**
		 * @type {number}
		 */
		this.subtotal = 0;

		/**
		 * @type {string}
		 */
		this.updateUrl = '';
	}

	get builderItems () {
		return Array.from(this.children)?.filter(child => child.nodeName === 'X-BUILDERSET-ITEM');
	}

	// eslint-disable-next-line max-lines-per-function
	render () {
		const classes = {
			container: true,
			loading: this.loading
		};

		return html`
			<div class="${classMap(classes)}">
				${this.loading ? html`<x-loader class="loader" aspect-ratio-desktop="1600/1200" aspect-ratio-tablet="820/1100" aspect-ratio="390/740"></x-loader>` : nothing}
				<div class="inner-container">
					<div class="info">
						<div class="image">
							<img src="${this.image}" loading="lazy" class="image-source" />
						</div>
						${this.renderInformation()}
					</div>
					<div class="details">
						<div class="actions">
							<slot @slotchange="${this.onTriggerSlotChange}"></slot>
							<form action="${this.updateUrl}" method="POST" @submit="${this.handleSplitShipment}">
								<input type="hidden" name="Action" value="ADPM">
								<input type="hidden" name="Products[ 1 ]:code" value="${this.code}">
								<input type="hidden" name="Products[ 1 ]:quantity" value="1" />
								<input type="hidden" name="BuilderSetCode" value="${this.id}">
								<button type="submit" class="split-shipments-button">Split Shipments</button>
							</form>
						</div>
						<div class="details-right">
							<div class="pricing-label">Subtotal</div>
							<div class="pricing">
								${this.renderPricing()}
							</div>
						</div>
					</div>
				</div>
			</div>
		`;
	}

	renderPricing () {
		if (!this.hidePricing) {
			return html`
				<div class="pricing-subtotal">
					${this.formattedSubtotal}
				</div>
				<div class="pricing-each">
					${this.formattedPrice} ea
				</div>
			`;
		}

		return html`
			<div class="pricing-subtotal">
				<span class="hide-pricing"></span>
			</div>
		`;
	}

	/**
	 * Event handler used for the `slotchange` event for the trigger slot.
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	onTriggerSlotChange (e) {
		const triggerElements = e.target.assignedNodes({flatten: true})
			.filter(node => node.nodeName === 'X-BUILDERSET-ITEM');

		// Assign the open event to the anchor or button element inside the trigger slot
		for (const triggerElement of triggerElements) {
			triggerElement.addEventListener('loading', () => this.setLoading());
		}
	}

	renderInformation () {
		return html`
			<div class="info-name">
				${this.renderReplacementPart()}
				${this.renderInventoryStatus()}
				${this.descrip ? html`<div class="name">${this.descrip}</div>` : nothing}
				<div class="code">${this.code}</div>
			</div>
		`;
	}

	renderReplacementPart () {
		if (!this.replacedPart) {
			return nothing;
		}

		return html`
			<div class="replaced-part">
				<span class="replaced-part-icon" aria-hidden="true"></span>
				<span class="replaced-part-text">Replacement Part for:</span>
				${this.replacedPart}
			</div>
		`;
	}

	renderInventoryStatus () {
		if (this.inventoryStatus !== 'BACKORDERED') {
			return nothing;
		}

		return html`
			<div class="inventory-status">
				<span class="inventory-status-icon" aria-hidden="true"></span>
				<span class="inventory-status-text">Backordered</span>
				${this.backorderFormattedDate ? `<span class="inventory-status-eta">ETA: ${this.backorderFormattedDate}</span>` : nothing}
			</div>
		`;
	}

	setLoading () {
		this.loading = true;
	}

	handleSplitShipment () {
		this.setLoading();

		window.dataLayer.push({
			ecommerce: null
		});

		window.dataLayer.push({
			ecommerce: {
				add: {
					products: [
						{
							/* eslint-disable camelcase */
							item_id: this.code,
							item_name: this.name,
							/* eslint-enable camelcase */
							price: this.price,
							quantity: 1
						}
					]
				},
				currencyCode: window.mivaJS?.Currency_Code
			},
			event: 'addToCart'
		});
	}
};

defineCustomElement('x-builderset-group', BuilderSetGroup);
